import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ccv2Fetch, getCcv2CurrentUser } from '../../services/callcenterV2';
import { formatDateTimeSeconds, saoPauloDayStartUtcIso, saoPauloDayEndUtcIso } from '../../utils/datetime';
import '../../components/CallcenterV2/ccv2-components.css';
import './config-styles.css';

const PAGE_SIZE = 50;

const SOURCE_LABELS = {
  pause_reasons: 'Motivos de Pausa',
  work_schedule: 'Escalas',
  metrics_catalog: 'Catálogo de Métricas',
};

const ACTION_LABELS = {
  CREATE: 'Criação',
  UPDATE: 'Atualização',
  DELETE: 'Exclusão',
  SEED: 'Seed',
};

function formatDate(iso) {
  if (!iso) return '—';
  return formatDateTimeSeconds(iso, '—');
}

function AuditLogRow({ item, expanded, onToggle }) {
  const sourceLabel = SOURCE_LABELS[item.source] ?? item.source;
  const actionLabel = ACTION_LABELS[item.action] ?? item.action;

  const actionBadgeClass = {
    CREATE: 'ccv2-p5-badge ccv2-p5-badge-active',
    UPDATE: 'ccv2-p5-badge',
    DELETE: 'ccv2-p5-badge ccv2-p5-badge-inactive',
  }[item.action] ?? 'ccv2-p5-badge';

  return (
    <>
      <tr
        className="ccv2-audit-row"
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
      >
        <td style={{ fontSize: '0.78rem', color: '#64748b', fontFamily: 'monospace' }}>
          {formatDate(item.performedAt)}
        </td>
        <td>
          <span style={{ fontSize: '0.78rem', color: '#475569' }}>{sourceLabel}</span>
        </td>
        <td>
          <span className={actionBadgeClass} style={{ fontSize: '0.72rem' }}>{actionLabel}</span>
        </td>
        <td style={{ fontSize: '0.78rem', color: '#64748b' }}>
          {item.performedByUserId ?? '—'}
        </td>
        <td style={{ fontSize: '0.78rem', color: '#94a3b8', fontFamily: 'monospace' }}>
          {item.requestIp ?? '—'}
        </td>
        <td style={{ fontSize: '0.75rem', color: '#6366f1' }}>
          {expanded ? '▲' : '▼'} detalhes
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} style={{ padding: '0.5rem 1rem 1rem', background: '#f8fafc' }}>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontWeight: 600, fontSize: '0.75rem', color: '#475569', marginBottom: 4 }}>
                  Antes
                </div>
                <pre style={{ fontSize: '0.72rem', background: '#f1f5f9', padding: '0.5rem', borderRadius: 6, overflow: 'auto', maxHeight: 200 }}>
                  {item.oldValues ? JSON.stringify(item.oldValues, null, 2) : '(vazio)'}
                </pre>
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontWeight: 600, fontSize: '0.75rem', color: '#475569', marginBottom: 4 }}>
                  Depois
                </div>
                <pre style={{ fontSize: '0.72rem', background: '#f0fdf4', padding: '0.5rem', borderRadius: 6, overflow: 'auto', maxHeight: 200 }}>
                  {item.newValues ? JSON.stringify(item.newValues, null, 2) : '(vazio)'}
                </pre>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function AuditLog() {
  const user = getCcv2CurrentUser();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // Filters
  const [source, setSource] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [userId, setUserId] = useState('');

  const cursorStackRef = useRef([]);

  const buildQuery = useCallback((cursor) => {
    const params = new URLSearchParams();
    if (source) params.set('source', source);
    if (from) params.set('from', saoPauloDayStartUtcIso(from));
    if (to) params.set('to', saoPauloDayEndUtcIso(to));
    if (userId) params.set('user_id', userId);
    params.set('limit', String(PAGE_SIZE));
    if (cursor) params.set('cursor', cursor);
    return params.toString();
  }, [source, from, to, userId]);

  const loadPage = useCallback(async (cursor) => {
    setLoading(true);
    setError(null);
    try {
      const qs = buildQuery(cursor);
      const data = await ccv2Fetch(`/v2/callcenter/audit?${qs}`);
      setItems(data.items ?? []);
      setTotal(data.total ?? 0);
      setNextCursor(data.nextCursor ?? null);
      setHasMore(Boolean(data.nextCursor));
    } catch (err) {
      setError(err?.message ?? 'Erro ao carregar auditoria');
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  // Load on mount and filter change
  useEffect(() => {
    cursorStackRef.current = [];
    loadPage(null);
  }, [loadPage]);

  const handleNextPage = () => {
    if (!nextCursor) return;
    cursorStackRef.current.push(nextCursor);
    loadPage(nextCursor);
  };

  const handlePrevPage = () => {
    cursorStackRef.current.pop();
    const prev = cursorStackRef.current[cursorStackRef.current.length - 1] ?? null;
    loadPage(prev);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    cursorStackRef.current = [];
    loadPage(null);
  };

  const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id);

  return (
    <div className="ccv2-page-container ccv2-p5-container">
      <div className="ccv2-page-header">
        <div className="ccv2-page-title-area">
          <h1>Log de Auditoria</h1>
          <p>Histórico de ações administrativas do Call Center V2</p>
        </div>
        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
          {total > 0 && `${total} registro${total !== 1 ? 's' : ''} encontrado${total !== 1 ? 's' : ''}`}
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="ccv2-p5-section" style={{ padding: '1rem 1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Origem</label>
            <select
              value={source}
              onChange={e => setSource(e.target.value)}
              style={{ padding: '0.4rem 0.75rem', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.85rem' }}
            >
              <option value="">Todas</option>
              {Object.entries(SOURCE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>De</label>
            <input
              type="date"
              value={from}
              onChange={e => setFrom(e.target.value)}
              style={{ padding: '0.4rem 0.75rem', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.85rem' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Até</label>
            <input
              type="date"
              value={to}
              onChange={e => setTo(e.target.value)}
              style={{ padding: '0.4rem 0.75rem', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.85rem' }}
            />
          </div>

          {user.isManager && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>ID do Usuário</label>
              <input
                type="number"
                value={userId}
                onChange={e => setUserId(e.target.value)}
                placeholder="Filtrar por usuário"
                style={{ padding: '0.4rem 0.75rem', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.85rem', width: 160 }}
              />
            </div>
          )}

          <button
            type="submit"
            className="ccv2-p5-btn ccv2-p5-btn-primary"
            disabled={loading}
            style={{ padding: '0.45rem 1.25rem' }}
          >
            {loading ? 'Carregando…' : 'Filtrar'}
          </button>

          <button
            type="button"
            className="ccv2-p5-btn ccv2-p5-btn-secondary"
            onClick={() => { setSource(''); setFrom(''); setTo(''); setUserId(''); }}
            style={{ padding: '0.45rem 1.25rem' }}
          >
            Limpar
          </button>
        </div>
      </form>

      {/* Error */}
      {error && (
        <div className="ccv2-p5-section" style={{ color: '#ef4444', padding: '0.75rem 1.5rem' }}>
          ⚠️ {error}
        </div>
      )}

      {/* Table */}
      <div className="ccv2-p5-section" style={{ padding: 0, overflow: 'auto' }}>
        {loading && items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
            Carregando…
          </div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
            Nenhum registro encontrado para os filtros selecionados.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: '0.75rem' }}>Data/Hora</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: '0.75rem' }}>Origem</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: '0.75rem' }}>Ação</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: '0.75rem' }}>Usuário ID</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: '0.75rem' }}>IP</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: '0.75rem' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <AuditLogRow
                  key={`${item.source}-${item.id}`}
                  item={item}
                  expanded={expandedId === item.id}
                  onToggle={() => toggleExpand(item.id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {(hasMore || cursorStackRef.current.length > 0) && (
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', padding: '1rem' }}>
          {cursorStackRef.current.length > 0 && (
            <button
              className="ccv2-p5-btn ccv2-p5-btn-secondary"
              onClick={handlePrevPage}
              disabled={loading}
            >
              ← Anterior
            </button>
          )}
          {hasMore && (
            <button
              className="ccv2-p5-btn ccv2-p5-btn-primary"
              onClick={handleNextPage}
              disabled={loading}
            >
              Próxima →
            </button>
          )}
        </div>
      )}

      <div style={{ padding: '1rem 1.5rem' }}>
        <Link to="/callcenterV2/dashboard/manager" className="ccv2-p5-btn ccv2-p5-btn-secondary" style={{ textDecoration: 'none' }}>
          ← Voltar ao Dashboard
        </Link>
      </div>
    </div>
  );
}

export default AuditLog;
